// contexts/CartContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import CarritoTaller from '../service/Carrito';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartExpiration, setCartExpiration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCart();
  }, []);

  // 🔥 MODIFICADA: Función helper para cargar el carrito (NO crea uno nuevo)
  const loadCart = async () => {
    try {
      setLoading(true);
      const carritoData = await CarritoTaller.ObtenerCarrito();

      // 🔥 Verificar si el backend devolvió un carrito válido o vacío.
      // 🛡️ Defensa: ignorar carritos ya completados/cancelados que el backend
      // pudiera devolver por timing/caché tras un pago exitoso (evita mostrar
      // ítems ya pagados y un re-pago accidental). No-op si el backend ya filtra.
      const estadoCarrito = carritoData?.estado;
      if (!carritoData || !carritoData.items || carritoData.items.length === 0
          || estadoCarrito === 'completado' || estadoCarrito === 'cancelado') {
        setCartItems([]);
        setCartExpiration(null);
        setError(null);
        return;
      }
      
      const items = carritoData.items || [];
      setCartItems(Array.isArray(items) ? items : []);
      setCartExpiration(carritoData.expiraEn || null);
      setError(null);
    } catch (err) {
      console.error('Error al cargar carrito:', err);
      
      // 🔥 Si es un 404 o "no encontrado", no es un error real
      if (err.status === 404 || err.message?.includes('no encontrado')) {
        setCartItems([]);
        setCartExpiration(null);
        setError(null);
      } else {
        setError(err);
        setCartItems([]);
        setCartExpiration(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Agregar item al carrito y refrescar
  const addToCart = async (itemData) => {
    try {
      setLoading(true);
      // 🔥 Esta acción SÍ crea el carrito si no existe
      await CarritoTaller.AgregarItem(itemData);
      
      await loadCart();
      
      setError(null);
    } catch (err) {
      console.error('Error al agregar al carrito:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Eliminar item del carrito y refrescar
  const removeFromCart = async (itemIndex) => {
    try {
      setLoading(true);
      await CarritoTaller.EliminarItem(itemIndex);
      
      await loadCart();
      
      setError(null);
    } catch (err) {
      console.error('Error al eliminar del carrito:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Vaciar carrito y refrescar
  const clearCart = async () => {
    try {
      setLoading(true);
      await CarritoTaller.VaciarCarrito();
      
      setCartItems([]);
      setCartExpiration(null);
      setError(null);
    } catch (err) {
      console.error('Error al vaciar carrito:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Extender tiempo del carrito
  const extendCartTime = async () => {
    try {
      setLoading(true);
      const response = await CarritoTaller.ExtenderTiempo();
      
      await loadCart();
      
      setError(null);
      return response;
    } catch (err) {
      console.error('Error al extender tiempo:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Contar items (considerando quantity)
  const getCartCount = () => {
    if (!Array.isArray(cartItems)) return 0;
    
    return cartItems.reduce((total, item) => {
      return total + (item.quantity || 1);
    }, 0);
  };

  // Obtener número de items sin considerar cantidad
  const getCartItemsCount = () => {
    return Array.isArray(cartItems) ? cartItems.length : 0;
  };

  // Recargar carrito desde el servidor
  const refreshCart = async () => {
    await loadCart();
  };

  return (
    <CartContext.Provider 
      value={{
        cartItems,
        cartExpiration,
        loading,
        error,
        addToCart,
        removeFromCart,
        clearCart,
        extendCartTime,
        getCartCount,
        getCartItemsCount,
        refreshCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe usarse dentro de CartProvider');
  }
  return context;
};
